
import { BlogPostFormData } from "@/types/blog";
import { PublicationOptions } from "../PublicationOptions";
import { PublicationChecklist } from "../PublicationChecklist";
import { PostStatus } from "@/hooks/useBlogPosts";

interface PublishTabProps {
  formData: BlogPostFormData;
  onChange: (field: string, value: any) => void;
  isLoading?: boolean;
}

export const PublishTab: React.FC<PublishTabProps> = ({
  formData,
  onChange,
  isLoading = false
}) => {
  // Handle status change
  const handleStatusChange = (status: PostStatus) => {
    onChange("status", status);
    
    // If changing from scheduled to another status, clear scheduled date
    if (status !== 'scheduled' && formData.scheduled_for) {
      onChange("scheduled_for", null);
    }
  };
  
  // Handle scheduled date change
  const handleDateChange = (date: Date | null) => {
    onChange("scheduled_for", date);
  };

  // Generate checklist items with updated conditions
  const getChecklistItems = () => {
    return [
      { label: "Title is set", isComplete: Boolean(formData.title.trim()) },
      { label: "Content is added", isComplete: Boolean(formData.content.trim()) },
      { label: "Excerpt is added", isComplete: Boolean(formData.excerpt.trim()) },
      { label: "Featured image is set", isComplete: Boolean(formData.image_url) },
      { label: "Tags are added", isComplete: Boolean(formData.metadata?.tags?.length) },
      { label: "Categories are set", isComplete: Boolean(formData.metadata?.categories?.length) },
      { label: "SEO description is set", isComplete: Boolean(formData.metadata?.seo_description?.trim()) },
      { label: "Slug is set", isComplete: Boolean(formData.slug.trim()) },
      { label: "Content sources are added", isComplete: true }, // Always complete as sources are optional
      // Check if publication date is set appropriately based on status
      { 
        label: formData.status === 'published' && formData.published_at 
          ? `This post was published on ${formData.published_at.toLocaleDateString()}` 
          : "Publication date is scheduled",
        isComplete: formData.status === 'published' || 
                   (formData.status === 'scheduled' && Boolean(formData.scheduled_for))
      },
    ];
  };

  return (
    <div className="space-y-4">
      <PublicationOptions
        status={formData.status}
        scheduledFor={formData.scheduled_for}
        onStatusChange={handleStatusChange}
        onDateChange={handleDateChange}
        disabled={isLoading}
      />
      
      <PublicationChecklist
        items={getChecklistItems()}
      />
    </div>
  );
};
